import { useAuth } from "@/components/auth"
import { useGetDistributorEntitiesLazyQuery, useGetUsersLazyQuery } from "@/graphql"
import { useFilterParams } from "@/hooks/useFilterParams"
import { usePagination } from "@/hooks/usePagination"
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import Filter from "./components/Filter"
import List from "./components/List"
import "./index.less"

const DEFAULT_FILTERS: { [key: string]: string | boolean | string[] } = {
  selectedRegion: "All",
  selectedStatus: [],
  selectedEmail: "All",
  selectedSM: "All",
  selectedDistributor: "All",
  selectedName: "All",
  isBadgesOnly: false,
}

const ListBlock: FC<TeamListBlockInterface> = ({ role }) => {
  const { region } = useAuth()
  const [getUsers] = useGetUsersLazyQuery()
  const [getDistributors] = useGetDistributorEntitiesLazyQuery()
  const [loading, setLoading] = useState<boolean>(false)
  const [exportLoading, setExportLoading] = useState<boolean>(false)

  const { page, pageSize, setPage, setPageSize } = usePagination()
  const [searchParams] = useSearchParams()

  const { filters, setFilters, filtersKey } = useFilterParams(DEFAULT_FILTERS)

  // eslint-disable-next-line
  const [list, setList] = useState<any>()
  // eslint-disable-next-line
  const [listForExport, setListForExport] = useState<any>()
  const [totalItems, setTotalItems] = useState<number>(0)

  const downloadBtnRef = useRef<HTMLButtonElement & { link: HTMLAnchorElement | null }>(null)

  const createQueryFilters = () => {
    const statusConditions = []
    const selectedStatuses = filters.selectedStatus as string[]

    if (selectedStatuses && selectedStatuses.length > 0 && !selectedStatuses.includes("All")) {
      if (role === "Distributor Entity") {
        if (selectedStatuses.includes("Active")) {
          statusConditions.push({ blocked: { eq: false } })
          statusConditions.push({ blocked: { null: true } })
        }
        if (selectedStatuses.includes("Blocked")) {
          statusConditions.push({ blocked: { eq: true } })
        }
      } else {
        if (selectedStatuses.includes("Active")) {
          statusConditions.push({ appActivationDate: { notNull: true }, blocked: { eq: false } })
        }
        if (selectedStatuses.includes("Inactive")) {
          statusConditions.push({ appActivationDate: { null: true }, blocked: { eq: false } })
        }
        if (selectedStatuses.includes("Blocked")) {
          statusConditions.push({ blocked: { eq: true } })
        }
      }
    }

    const baseFilters = {
      ...(filters.selectedName !== "All" ? { name: { eq: filters.selectedName } } : {}),
      ...(filters.selectedEmail !== "All" ? { email: { eq: filters.selectedEmail } } : {}),

      ...(statusConditions.length > 0 ? { or: statusConditions } : {}),
    }

    // eslint-disable-next-line
    const roleSpecificFilters: any = {
      dsc: {
        role: { name: { eq: "DSC" } },
        dsc: {
          ...(filters.isBadgesOnly ? { ranking: { ne: null } } : {}),
          sales_manager: {
            name: { eq: filters.selectedSM !== "All" ? filters.selectedSM : undefined },
            distributor: {
              name: { eq: filters.selectedDistributor !== "All" ? filters.selectedDistributor : undefined },
              region: {
                name: { eq: filters.selectedRegion !== "All" ? filters.selectedRegion : region?.name || undefined },
              },
            },
          },
        },
      },

      sales_manager: {
        role: { name: { eq: "Sales Manager" } },
        sales_manager: {
          distributor: {
            name: { eq: filters.selectedDistributor !== "All" ? filters.selectedDistributor : undefined },
            region: {
              name: { eq: filters.selectedRegion !== "All" ? filters.selectedRegion : region?.name || undefined },
            },
          },
        },
      },

      distributor: {
        role: { name: { eq: "Distributor" } },
        distributor: {
          name: { eq: filters.selectedDistributor !== "All" ? filters.selectedDistributor : undefined },
          region: {
            name: { eq: filters.selectedRegion !== "All" ? filters.selectedRegion : region?.name || undefined },
          },
        },
      },

      distributor_entity: {
        name: { eq: filters.selectedDistributor !== "All" ? filters.selectedDistributor : undefined },
        region: { name: { eq: filters.selectedRegion !== "All" ? filters.selectedRegion : region?.name || undefined } },
      },

      region: {
        role: { name: { eq: "Region" } },
        region: { name: { eq: filters.selectedRegion !== "All" ? filters.selectedRegion : region?.name || undefined } },
      },
    }

    const specificFilters = roleSpecificFilters[role?.toLowerCase()?.replace(" ", "_")]
    return {
      ...baseFilters,
      ...specificFilters,
    }
  }

  const createList = async (isForExport = false) => {
    setLoading(true)

    try {
      if (list && !isForExport) {
        setList(undefined)
      }
      // eslint-disable-next-line
      let newList: any
      const pagination = {
        pageSize: isForExport ? 1000 : pageSize,
        page: isForExport ? undefined : page,
      }

      if (role !== "Distributor Entity") {
        newList = await getUsers({
          fetchPolicy: "no-cache",
          errorPolicy: "all",
          variables: {
            sort: "name:asc",
            filter: createQueryFilters(),
            pagination,
          },
        })
      } else {
        newList = await getDistributors({
          fetchPolicy: "no-cache",
          errorPolicy: "all",
          variables: {
            sort: "name:asc",
            filter: createQueryFilters(),
            pagination,
          },
        })
      }

      let totalItems
      let finalData

      if (newList.data?.usersPermissionsUsers) {
        totalItems = newList.data.usersPermissionsUsers.meta.pagination.total || 0
        finalData = newList.data.usersPermissionsUsers.data
      } else if (newList.data?.distributors) {
        totalItems = newList.data.distributors.meta.pagination.total || 0
        finalData = newList.data.distributors.data
      }

      if (finalData && !isForExport) {
        // eslint-disable-next-line
        setTotalItems(totalItems!)
        setList(finalData)
        return
      } else {
        return finalData
      }
    } finally {
      setLoading(false)
    }
  }

  const createRoleFiltersList = () => {
    const list = []
    if (filters.selectedName !== "All") {
      list.push({
        option: filters.selectedName as string,
        type: "name",
        backgroundColor: "rgba(215, 163, 171, 0.15)",
        color: "#D7A3AB",
      })
    }
    if (filters.selectedEmail !== "All") {
      list.push({
        option: filters.selectedEmail as string,
        type: "email",
        backgroundColor: "rgba(68, 195, 187, 0.15)",
        color: "#44C3BB",
      })
    }
    if (filters.selectedSM !== "All") {
      list.push({
        option: filters.selectedSM as string,
        type: "Sales Manager",
        backgroundColor: "rgba(11, 214, 42, 0.15)",
        color: "#2bb350",
      })
    }
    if (filters.selectedDistributor !== "All") {
      list.push({
        option: filters.selectedDistributor as string,
        type: "Distributor",
        backgroundColor: "rgba(50, 105, 168, 0.15)",
        color: "#3269a8",
      })
    }
    return list
  }

  const roleFilters = createRoleFiltersList()

  const createExportList = async () => {
    setExportLoading(true)
    const list = await createList(true)

    const headerRow = [
      role !== "Distributor Entity" ? "NAME" : null,
      role !== "Distributor Entity" ? "EMAIL" : null,
      role === "DSC" ? "SALES MANAGER" : null,
      role !== "Region" ? "DISTRIBUTOR" : null,
      "REGION",
      role !== "Distributor Entity" ? "STATUS" : null,
    ].filter((entity): entity is string => entity !== null)

    // eslint-disable-next-line
    const rows = (list ?? []).map((item: any) => {
      const attrs = item?.attributes ?? {}

      return [
        role !== "Distributor Entity" ? attrs.name || "-" : null,
        role !== "Distributor Entity" ? attrs.email || "-" : null,
        role === "DSC" ? attrs.dsc?.data?.attributes?.sales_manager?.data?.attributes?.name || "-" : null,
        role !== "Region"
          ? (role === "Distributor Entity" && attrs.name) ||
            attrs.distributor?.data?.attributes?.name ||
            attrs.sales_manager?.data?.attributes?.distributor?.data?.attributes?.name ||
            attrs.dsc?.data?.attributes?.sales_manager?.data?.attributes?.distributor?.data?.attributes?.name ||
            "-"
          : null,
        attrs.region?.data?.attributes?.name ||
          attrs.distributor?.data?.attributes?.region?.data?.attributes?.name ||
          attrs.sales_manager?.data?.attributes?.distributor?.data?.attributes?.region?.data?.attributes?.name ||
          attrs.dsc?.data?.attributes?.sales_manager?.data?.attributes?.distributor?.data?.attributes?.region?.data
            ?.attributes?.name ||
          "-",
        role !== "Distributor Entity"
          ? attrs.blocked
            ? "blocked"
            : attrs.appActivationDate
              ? "active"
              : "inactive"
          : null,
      ].filter((entity): entity is string => entity !== null)
    })

    const finalExportList = [headerRow, ...rows]

    setExportLoading(false)
    setListForExport(finalExportList)
  }

  useLayoutEffect(() => {
    const hasStatusInUrl = searchParams.has("selectedStatus")

    if (!hasStatusInUrl) {
      setFilters(
        {
          ...filters,
          selectedStatus: role === "Distributor Entity" ? ["Active"] : ["Active", "Inactive"],
        },
        false
      )
    }
    // eslint-disable-next-line
  }, [role])

  useEffect(() => {
    setListForExport(undefined)
    createList() // eslint-disable-next-line
  }, [role, pageSize, page, filtersKey])

  useEffect(() => {
    // eslint-disable-next-line
    listForExport && downloadBtnRef.current!.link!.click()
  }, [listForExport])

  return (
    <div className={"block-wrapper"}>
      <Filter
        {...{
          roleFilters,
          filters,
          setFilters,
          role,
          createList,
        }}
      />
      <List
        {...{
          list,
          role,
          setPage,
          page,
          setPageSize,
          pageSize,
          totalItems,
          loading,
          setLoading,
          filters,
          setFilters,
          createQueryFilters,
          listForExport,
          exportLoading,
          downloadBtnRef,
          createExportList,
        }}
      />
    </div>
  )
}

export default ListBlock
